import React, { useState } from 'react'
import type { Block, Variable } from '../../editor/types'

interface SliderBlockProps {
  block: Block
  variableValues: Record<string, number | string | boolean>
  variables: Variable[]
  onSetVariableValue: (name: string, value: number | string | boolean) => void
}

export default function SliderBlock({ block, variableValues, variables, onSetVariableValue }: SliderBlockProps) {
  const varName = block.variable || ''
  const variable = variables.find((v) => v.name === varName)
  const min = variable?.min ?? 0
  const max = variable?.max ?? 100
  const step = variable?.step ?? 1
  const value = Number(variableValues[varName] ?? variable?.default ?? 0)
  const percentage = max - min > 0 ? ((value - min) / (max - min)) * 100 : 0

  const accent = block.accent || 'var(--accent, #5048e5)'
  const config = (block.config || {}) as Record<string, unknown>
  const suffix = (config.suffix as string) ?? '%'
  const showValueLabel = (config.showValueLabel as boolean) !== false
  const [touched, setTouched] = useState(false)

  return (
    <div className="flex flex-col gap-4">
      {/* Label + value */}
      <div className="flex justify-between items-end">
        {block.label && (
          <label className="text-sm font-semibold" style={{ color: 'var(--block-text, #0f172a)' }}>{block.label}</label>
        )}
        {showValueLabel && (
          <span className="text-3xl font-mono font-bold" style={{ color: accent }}>{value}{suffix}</span>
        )}
      </div>

      {/* Custom slider track — enlarged hit area */}
      <div className="relative h-10 w-full flex items-center" style={{ margin: '4px 0' }}>
        {/* Visual track */}
        <div className="absolute left-0 right-0 h-2 top-1/2 -translate-y-1/2">
          <div className="absolute inset-0 bg-slate-200 rounded-full" />
          <div
            className="absolute h-full rounded-full"
            style={{ width: `${percentage}%`, backgroundColor: accent }}
          />
          {/* Visual thumb */}
          <div
            className="absolute top-1/2 size-5 bg-white border-2 rounded-full shadow-lg pointer-events-none"
            style={{ left: `calc(${percentage}% - 10px)`, transform: 'translateY(-50%)', borderColor: accent, ...(!touched ? { animation: 'knob-pulse 1.5s ease-in-out infinite', boxShadow: `0 0 0 4px color-mix(in srgb, ${accent} 20%, transparent)` } : {}) }}
          />
        </div>
        {/* Invisible range input — full height for easy grab */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => { if (!touched) setTouched(true); onSetVariableValue(varName, Number(e.target.value)) }}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          style={{ margin: 0 }}
        />
      </div>
    </div>
  )
}
